import React, { useEffect, useMemo, useState } from 'react';

import MerchantSelect from '@components/operator/MerchantSelect';
import InboundEditForm from '@components/shared/inbounds/InboundEdit';
import { useOperatorAuth } from '@contexts/operator/OperatorAuthContext';
import { useOperatorData } from '@contexts/operator/OperatorDataContext';
import { useOperatorInboundService } from '@hooks/operator/inbounds/useOperatorInboundService';
import useRecordMerchantSync from '@hooks/operator/useRecordMerchantSync';
import { useOperatorProductService } from '@hooks/operator/products/useOperatorProductService';
import { useOperatorSupplierService } from '@hooks/operator/suppliers/useOperatorSupplierService';
import { useParams } from 'react-router-dom';

import operatorInventoryAttributeDefinitionService from '@services/operator/inventoryAttributeDefinitionService';

interface InventoryTypeOption {
  id: string;
  name?: string;
  [key: string]: unknown;
}

interface MerchantInventoryType {
  merchant_id: string;
  enabled?: boolean;
  inventory_type: InventoryTypeOption;
}

interface InventoryAttributeDefinition {
  id?: string;
  name: string;
  [key: string]: unknown;
}

const OperatorInboundEdit: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const { operator } = useOperatorAuth();
  const operatorWarehouseName = (operator?.warehouse as { title?: string } | undefined)?.title;

  const { merchantInventoryTypes, currentMerchant } = useOperatorData();
  const { handleRecordLoaded, merchantDataLoading } = useRecordMerchantSync();

  // currentMerchant 在 edit 模式下會被 useRecordMerchantSync 切到 record 所屬 merchant，
  // 在 new 模式下由 MerchantSelect 設定。Reference data 永遠跟著 currentMerchant 走。
  const effectiveMerchantId = currentMerchant?.id ?? null;

  const inventoryTypes = useMemo(() => {
    if (!effectiveMerchantId) return [];
    return (merchantInventoryTypes as MerchantInventoryType[] | undefined ?? [])
      .filter(mit => mit.merchant_id === effectiveMerchantId && mit.enabled !== false)
      .map(mit => mit.inventory_type);
  }, [merchantInventoryTypes, effectiveMerchantId]);

  const allInventoryTypes = useMemo(() => {
    if (!effectiveMerchantId) return [];
    return (merchantInventoryTypes as MerchantInventoryType[] | undefined ?? [])
      .filter(mit => mit.merchant_id === effectiveMerchantId)
      .map(mit => mit.inventory_type);
  }, [merchantInventoryTypes, effectiveMerchantId]);

  const [inventoryAttributeDefinitions, setInventoryAttributeDefinitions] = useState<InventoryAttributeDefinition[]>([]);

  const supplierService = useOperatorSupplierService({ merchantId: effectiveMerchantId ?? undefined });

  useEffect(() => {
    if (!effectiveMerchantId) {
      setInventoryAttributeDefinitions([]);
      return;
    }
    let cancelled = false;
    operatorInventoryAttributeDefinitionService
      .fetchList({ merchant_id: effectiveMerchantId })
      .then((result: { success?: boolean; data?: InventoryAttributeDefinition[] }) => {
        if (cancelled) return;
        setInventoryAttributeDefinitions(result?.data ?? []);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        console.error('Failed to fetch inventory attribute definitions', error);
        setInventoryAttributeDefinitions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [effectiveMerchantId]);

  // 編輯模式：wrapper 由 onInboundLoaded 拿到 record's merchant_id 後同步 context，
  // 整個過程 merchantDataLoading 為 true 擋住 form 渲染，避免閃錯的下拉。
  if (id) {
    return (
      <InboundEditForm
        useInboundService={useOperatorInboundService}
        useProductService={useOperatorProductService}
        supplierService={supplierService}
        role="operator"
        operatorWarehouseName={operatorWarehouseName}
        inventoryTypes={inventoryTypes}
        allInventoryTypes={allInventoryTypes}
        inventoryAttributeDefinitions={inventoryAttributeDefinitions}
        onInboundLoaded={handleRecordLoaded}
        merchantDataLoading={merchantDataLoading}
      />
    );
  }

  // 新增模式：MerchantSelect 控制 merchant；record 還不存在，不需要 sync
  return (
    <MerchantSelect>
      <InboundEditForm
        useInboundService={useOperatorInboundService}
        useProductService={useOperatorProductService}
        supplierService={supplierService}
        role="operator"
        operatorWarehouseName={operatorWarehouseName}
        inventoryTypes={inventoryTypes}
        allInventoryTypes={allInventoryTypes}
        inventoryAttributeDefinitions={inventoryAttributeDefinitions}
      />
    </MerchantSelect>
  );
};

export default OperatorInboundEdit;
